/* prettier-ignore */
import { CertificateState, RIASRecordType, SubscriptionState, RIASRoles } from "~/lib/types";
import { padId, schemeData, Subscription } from "~/lib";
/* prettier-ignore */
import { updateDB, putItem,	getProfile,	getByUser,	RIASTABLE,	runQueryCommand, getCounter, queryByPK } from "~/_server/dynamoDB";
import { sendEmail, sendDevEmail } from "~/_server/server.email";
import { SEND_TO_ADMINS, SEND_TO_DEV } from "~/lib/constants";
import { createInvoice, getInvoiceByNumber, getInvoiceUrl, markInvoiceAsPaid } from "~/_server/accounts";
import { getServerEnv } from "~/_server/server";
import { getByRowTypeServer } from "~/_server/dynamoQueries";
import { getUserRecord } from "~/_server/functions";
import { getUserFromEvent } from "~/_server/auth.server";
import { ensureCognitoUser } from "~/_server/cognito";
import { ADMIN } from "~/_server/constants";
import { checkDescriptionAgainstPlots } from "~/_server/descriptionCheck.server";

// Admin repair for a certificate that was published but never invoiced (the
// Xero call failed at publish time). Mirrors the wizard's publish step: same
// line item, same suffixed invoice number, then links the invoice to the
// certificate so paying it flips the status to PAID as normal.
export const createMissingCertificateInvoice = async (
	pk: string,
	sk: string,
): Promise<{ invoiceId?: string; invoiceNumber?: string; error?: string }> => {
	"use server";

	const admin = await getUserFromEvent();

	if (!admin?.groups?.includes(ADMIN)) return { error: "Unauthorized" };

	const userId = parseInt(pk.replace("USER#", ""), 10);
	const certificate = await getUserRecord(userId, sk);
	const user = await getUserRecord(userId, padId(userId, RIASRecordType.USER));

	if (!certificate?.sk) return { error: "Certificate not found" };
	if (certificate.invoiceId) return { error: "Certificate already has an invoice" };
	if (certificate.status !== CertificateState.PUBLISHED) return { error: `Certificate is ${certificate.status}, not published` };
	if (!certificate.cost) return { error: "Certificate has no cost recorded" };
	if (!user?.email) return { error: "Certifier has no email address" };

	const stage = certificate.meta?.stages?.length ?? 0;
	const amendment = certificate.meta?.amendments?.length ?? 0;
	const suffix = `${stage ? `-S${stage}` : ""}${amendment ? `-A${amendment}` : ""}`;

	const { invoiceId, invoiceNumber } = await createInvoice(
		user.email,
		`${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || user.name,
		[
			{
				description: `Certificate Fee (#${certificate.sk}) - ${certificate.name}`,
				quantity: 1.0,
				unitAmount: certificate.cost,
				accountCode: "202",
				lineAmount: certificate.cost,
			},
		],
		certificate.riasId,
		"",
		suffix,
	);

	if (!invoiceId) return { error: "Xero did not return an invoice - see the server log" };

	await updateDB(pk, sk, { invoiceId });

	return { invoiceId, invoiceNumber };
};

const addYearSeconds = (seconds: number, years: number = 1) => {
	const date = new Date(seconds * 1000);
	date.setFullYear(date.getFullYear() + years);
	return Math.floor(date.getTime() / 1000);
};

// user.targetScheme is stored camelCase, straight from the registration form
// (Register.tsx radio values). Every other part of the app - subscription
// templates, certificate scheme lookups, exports - expects the kebab-case
// slug instead, so new subscriptions must be created with the converted form.
const TARGET_SCHEME_TO_SLUG: Record<string, string> = {
	s6Domestic: "section-6-domestic",
	s6NonDomestic: "section-6-non-domestic",
	s6NonDomesticAdv: "section-6-non-domestic-advanced",
	CPD: "cpd",
};

// Once a domestic certificate has been paid for, checks that the plot count in its
// description of works adds up to the plots listed, and tells the admins if it does not.
// It never blocks anything and never throws: the payment has already been recorded.
const flagPlotCountMismatch = async (certificate: any) => {
	try {
		if (certificate.scheme !== "section-6-domestic") return;

		const plots = certificate.meta?.plots?.length ?? 0;
		const description = certificate.meta?.descriptionOfWorks ?? "";

		const check = await checkDescriptionAgainstPlots(description, plots);

		if (check.status !== "mismatch") return;

		// Same id string the /certificate/[id] route parses: C0002956-Stage2-Amendment1
		const stages = certificate.meta?.stages?.length ?? 0;
		const amendments = certificate.meta?.amendments?.length ?? 0;

		let certificateId = certificate.riasId ? `C${padId(certificate.riasId)}` : certificate.sk;

		if (certificate.riasId && stages) certificateId += `-Stage${stages}`;
		if (certificate.riasId && amendments) certificateId += `-Amendment${amendments}`;

		const certifier = await getProfile(parseInt(String(certificate.pk).split("#")[1])).catch(() => undefined);

		await sendEmail({
			templateName: "certificate-description-check",
			props: {
				certificateId,
				certificateName: certificate.name ?? "",
				certifier: certifier ? `${certifier.firstName} ${certifier.lastName} (${certifier.email})` : certificate.pk,
				description,
				plots: check.plots,
				stated: check.stated,
				certain: check.certain,
				kind: amendments ? "amendment" : stages > 1 ? `stage ${stages}` : stages === 1 ? "stage 1" : "initial",
				url: `https://rias-regs.co.uk/certificate/${certificateId}`,
			},
			to: SEND_TO_ADMINS,
			subject: `${check.certain ? "" : "Possible: "}Certificate ${certificateId} plot count (${check.plots}) vs description (${check.stated})`,
		});
	} catch (err) {
		console.error("flagPlotCountMismatch failed", err);
	}
};

export const paymentRecieved = async (invoiceId: string) => {
	"use server";
	console.log("Payment received for invoice", invoiceId);

	let emailTemplate: string | null = null;

	const r = await runQueryCommand({
		TableName: RIASTABLE,
		IndexName: "gsiInvoiceId",
		KeyConditionExpression: "invoiceId = :id",
		ExpressionAttributeValues: { ":id": invoiceId },
	});

	console.log("Matched invoice:", r);

	if (r?.items?.[0]) {
		const item = r.items[0];
		const recordType = item?.recordType;

		switch (recordType) {
			case "CERTIFICATE":
				await updateDB(item.pk, item.sk, { status: CertificateState.PAID, paid: Math.floor(Date.now() / 1000) });
				emailTemplate = "certificate-paid";

				// Stripe can deliver the same payment twice; only look at it the first time.
				if (item.status !== CertificateState.PAID) await flagPlotCountMismatch(item);
				break;

			case "SUBSCRIPTION":
				let update: { status?: string; expires?: number; joined?: number } = {};

				switch (item.status) {
					case SubscriptionState.ACTIVE:
					case SubscriptionState.RENEWING:
						update.status = SubscriptionState.ACTIVE;
						update.expires = addYearSeconds(item.expires);
						break;

					case SubscriptionState.SUSPENDED:
					case SubscriptionState.ASSESSING:
					case SubscriptionState.PENDING_BODY:
					case SubscriptionState.PRACTICAL:
						update.expires = addYearSeconds(item.expires);
						break;

					case SubscriptionState.PENDING:
					case SubscriptionState.APPLYING:
						// They become a certifier now (OK to re-purpose SubscriptionState.ACTIVE - it's just a string)
						await updateDB(
							item.pk,
							padId(parseInt(item.pk.split("#")[1]), RIASRecordType.USER),
							{ role: RIASRoles.CERTIFIER, status: SubscriptionState.ACTIVE },
						);

						const now = Math.floor(Date.now() / 1000);

						// CPD goes by two slugs - "cpd-membership" and "cpd", which is what a new
						// application records - so ask the scheme rather than match one spelling.
						// Matching only "cpd-membership" put every new CPD member into assessing
						// with a one-year expiry.
						const isCPD = !!schemeData(item.scheme)?.notScheme;

						update.status = isCPD ? SubscriptionState.CPD : SubscriptionState.ASSESSING;
						update.expires = addYearSeconds(now, isCPD ? 20 : 1); // CPD is 'infinite', others are 1 year
						update.joined = now;

						emailTemplate = "subscription-complete";
						break;
				}

				if (Object.keys(update).length) {
					await updateDB(item.pk, item.sk, update);
				}

				if (emailTemplate) {
					try {
						const user = await getProfile(parseInt(item.pk.split("#")[1]));
						const scheme = schemeData(item.scheme);

						let invoiceLink = await getInvoiceUrl(invoiceId);

						if (!("error" in invoiceLink)) {
							await sendEmail({
								templateName: emailTemplate,
								props: {
									// SubscriptionComplete reads userFirstName and a plain
									// schemeName string - not "name", and not the scheme object.
									userFirstName: user.firstName,
									schemeName: scheme?.name ?? item.scheme,
									invoiceLink: invoiceLink.onlineInvoiceUrl,
								},
								to: user.email,
								bcc: SEND_TO_DEV,
							});
						}
					} catch (err) {
						await sendDevEmail(
							"Error sending email after payment",
							JSON.stringify(err),
						);
					}
				}
				break;
		}
	} else {
		await sendDevEmail(
			"Payment received but no matching record",
			`Invoice ID: ${invoiceId}`,
		);
	}
};

export const verifyUser = async (userId: number) => {
	"use server";

	try {
		const user = await getProfile(userId);

		if (!user?.targetScheme) {
			console.log("Error verifying user: missing targetScheme", { userId });
			return { success: false, error: "This user has no target scheme set on their profile." };
		}

		// targetScheme is stored as the registration form's own value ("CPD",
		// "s6Domestic"), which schemeData() does not recognise. Normalise once,
		// here, and use the slug for everything below - an unconverted "CPD" threw
		// "Scheme not found" out of generateSubscriptionName and left the user
		// half-verified with no subscription.
		const schemeSlug = TARGET_SCHEME_TO_SLUG[user.targetScheme] ?? user.targetScheme;

		// Set user record to certifier
		const updateResult = await updateDB(`USER#${userId}`, padId(userId, RIASRecordType.USER), {
			status: "payment-pending",
			role: schemeSlug == "cpd" ? RIASRoles.CPD : RIASRoles.CERTIFIER,
		});

		if ((updateResult as any)?.success === false) {
			console.log("Error verifying user: status update failed", { userId, error: (updateResult as any).error });
			return { success: false, error: "Could not update the user's status. Please try again." };
		}

		const subscriptions = (await getByUser("SUBSCRIPTION", userId, -1).then(
			r => r.items ?? [],
		)) as Subscription[];

		if (subscriptions.length === 0) {
			const name = await generateSubscriptionName( schemeSlug );
			
			console.log("Creating subscription for user", name);

			const newSub: Subscription = {
				pk: `USER#${userId}`,
				sk: padId(
					parseInt(name.split("-").pop() as string, 10),
					RIASRecordType.SUBSCRIPTION,
				),
				status: SubscriptionState.APPLYING,
				expires: 0,
				created: Math.floor(Date.now() / 1000),
				recordType: RIASRecordType.SUBSCRIPTION,
				name,
				scheme: schemeSlug,
			};

			await putItem(newSub);

			subscriptions.push(newSub);
		}

		for (const sub of subscriptions) {
			if (
				sub.status == SubscriptionState.APPLYING ||
				sub.status == SubscriptionState.VALIDATING
			) {
				try {
					// Create an invoice for joining and the first year's subs
					const invoiceId = await createSubscriptionInvoice(
						sub.scheme,
						parseInt(sub.sk?.replace("SUBSCRIPTION#", "") as string, 10),
						userId,
						true,
						new Date().getFullYear(),
					);

					if (invoiceId) {
						// Set subscription to paying
						// The reminder cron measures from here. Nothing else on the record
						// dates the moment payment fell due - putItem does not stamp
						// created, and registration can precede verification by weeks.
						await updateDB(sub.pk as string, sub.sk as string, {
							expires: 0,
							status: SubscriptionState.PENDING,
							pendingSince: Math.floor(Date.now() / 1000),
							invoiceId,
						});

						const user = await getProfile(userId);
						const scheme = schemeData( sub.scheme );

						console.log("User verified, invoice created:", invoiceId, "for user:", user.name, "scheme:", scheme);

						// The email below invites them to join, so the account has to
						// exist by the time it lands. Failure is logged, not thrown -
						// the access icon on the users page remains as a fallback.
						const cognitoSub = await ensureCognitoUser(user);

						if (!cognitoSub) {
							console.log("Verified without a Cognito account - create it from the users page", { userId });
						}

						// Send an email to the user with next steps (payment)
						await sendEmail({
							templateName: "application-verified",
							props: {
								user: { name: user.firstName },
								// schemeData returns null for a slug it does not recognise, and
								// one legacy subscription still carries "CPD" rather than "cpd".
								schemeName: scheme?.name ?? sub.scheme,
								// notScheme marks CPD membership in schemes.json: a one-off
								// joining fee, no renewal, no Approved Body.
								isCPD: !!scheme?.notScheme,
								login: "https://rias-regs.co.uk/login",
							},
							to: user.email,
							bcc: SEND_TO_ADMINS,
						});
					}
				} catch (err) {
					console.log("Error verifying user", err);
				}
			}
		}

		return { success: true };
	} catch (err) {
		console.log("Error verifying user", err);
		return { success: false, error: "An unexpected error occurred while verifying this user." };
	}
};

export const suspendUser = async (userId: number) => {
	"use server";

	try {
		const subscriptions = (await getByUser("SUBSCRIPTION", userId, -1).then(
			r => r.items ?? [],
		)) as Subscription[];

		for (const sub of subscriptions) {
			if (sub.status == SubscriptionState.ACTIVE) {
				await updateDB(sub.pk as string, sub.sk as string, {
					status: SubscriptionState.SUSPENDED,
				});
			}
		}

		await updateDB(`USER#${userId}`, padId(userId, RIASRecordType.USER), {
			status: "suspended",
			suspended: Date.now(),
		});

		return { success: true };
	} catch (err) {
		console.log("Error suspending user", err);
		return { success: false, error: "An unexpected error occurred while suspending this user." };
	}
};

export const reactivateUser = async (userId: number) => {
	"use server";

	try {
		const subscriptions = (await getByUser("SUBSCRIPTION", userId, -1).then(
			r => r.items ?? [],
		)) as Subscription[];

		for (const sub of subscriptions) {
			if (sub.status == SubscriptionState.SUSPENDED) {
				await updateDB(sub.pk as string, sub.sk as string, {
					status: SubscriptionState.ACTIVE,
				});
			}
		}

		await updateDB(`USER#${userId}`, padId(userId, RIASRecordType.USER), {
			status: "active",
			suspended: 0,
		});

		return { success: true };
	} catch (err) {
		console.log("Error reactivating user", err);
		return { success: false, error: "An unexpected error occurred while reactivating this user." };
	}
};

export const toggleUserCompliance = async (
	userId: number,
	category: "method" | "software",
	value: string,
) => {
	"use server";

	const user = await getProfile(userId);

	if (!user?.pk) {
		throw new Error("User not found");
	}

	const current: string[] = user.compliance?.[category] ?? [];
	const next = current.includes(value)
		? current.filter((v: string) => v !== value)
		: [...current, value];

	await updateDB(user.pk, user.sk, {
		compliance: { ...user.compliance, [category]: next },
	});

	return next;
};

export const createSubscriptionInvoice = async (
	schemeSlug: string,
	riasId: number,
	userId: number,
	incJoiningFee: boolean = false,
	year: number = new Date().getFullYear(),
) => {
	try {
		const user = await getProfile(userId);
		const scheme = schemeData(schemeSlug);

		if (scheme) {
			const lineItems: Record<string, any>[] = [];
			const isCPD = scheme.name == "CPD Membership";

			const description = isCPD
				? "CPD Membership"
				: `Certification of Design (${scheme.name})`;

			if (incJoiningFee) {
				// Logic for non RIAS members CPD fee.
				const isRIAS = (user.memberships?.rias ?? "").trim().length > 3;
				const joiningFee =
					isCPD && isRIAS ? scheme.joiningFeeRIASMember : scheme.joiningFee;

				lineItems.push({
					description: `Joining Fee - ${description} (#${riasId})`,
					quantity: 1.0,
					unitAmount: joiningFee ?? 0,
					accountCode: "203",
					lineAmount: joiningFee ?? 0,
				});
			}

			if ((scheme.renewalFee ?? 0) > 0) {
				lineItems.push({
					description: `Subscription Fee (${year}) - ${description} (#${riasId})`,
					quantity: 1.0,
					unitAmount: scheme.renewalFee ?? 0,
					accountCode: "201",
					lineAmount: scheme.renewalFee ?? 0,
				});
			}

			const { invoiceId } = await createInvoice(
				user.email,
				`${user.firstName} ${user.lastName}`,
				lineItems,
				riasId,
			);

			return invoiceId;
		} else {
			throw new Error("Scheme not found: " + schemeSlug);
		}
	} catch (err) {
		console.log("Error creating subscription invoice", err);
	}

	return null;
};

export const matchABExpiry = async () => {
	"use server";

	const subs = await getByRowTypeServer(
		RIASRecordType.SUBSCRIPTION,
		2000,
		"#expires,#approvedBodyId",
		{ "#expires": "expires", "#approvedBodyId": "approvedBodyId" },
	);

	const abs = await getByRowTypeServer(
		RIASRecordType.APPROVEDBODY,
		2000,
		"#expires,#sk,#pk",
		{ "#expires": "expires", "#sk": "sk", "#pk": "pk" },
	);

	const byPk = new Map(
		(abs?.items ?? []).map(item => [
			parseInt(item.pk.replace("APPROVEDBODY#", "")),
			item.expires,
		]) ?? [],
	);

	const result = (subs?.items ?? []).reduce((acc, item) => {
		const { approvedBodyId, expires } = item;

		// A body that exists but has never had an expiry set must be treated
		// like a missing one - comparing against undefined is always false and
		// left such bodies out of the BSD export.
		const current = byPk.get(parseInt(approvedBodyId));

		if (expires && approvedBodyId && (current == null || expires > current)) {
			if (!acc[approvedBodyId] || expires > acc[approvedBodyId]) {
				acc[approvedBodyId] = expires;
			}
		}
		return acc;
	}, {});

	for (const [approvedBodyId, expires] of Object.entries(result)) {
		const pk = `APPROVEDBODY#${approvedBodyId}`;
		const sk = `APPROVEDBODY#${approvedBodyId.toString().padStart(7, "0")}`;

		await updateDB(pk, sk, { expires });
	}

	return;
};

export const submitApprovedBody = async (
	subscription: Subscription,
	data: {
		address: Record<string, string>;
		coordinator: { name: string; email: string; telephone: string };
		deputy: { name: string; email: string; telephone: string };
	},
) => {
	"use server";

	const userId = parseInt( subscription.pk?.replace( "USER#", "" ) ?? "0" );

	await updateDB( subscription.pk as string, subscription.sk as string, {
		pendingAB: {
			address: { ...data.address, title: data.address.name },
			coordinator: data.coordinator,
			deputy: data.deputy,
			submitted: Math.floor( Date.now() / 1000 ),
		},
		status: SubscriptionState.PENDING_BODY,
	} );

	const user = await getProfile( userId );
	const scheme = schemeData( subscription.scheme );

	// "approved-body-submitted" is the admin notification ("X has been submitted
	// by ..."), so it carries the full name; "approved-body-request" is the
	// acknowledgement to the certifier, who is greeted by first name only.
	await sendEmail( {
		templateName: "approved-body-submitted",
		props: {
			user: { name: user?.name },
			applicationName: data.address.name,
			url: `https://rias-regs.co.uk/dashboard/admin/users/${userId}`,
		},
		to: SEND_TO_ADMINS,
	} );

	await sendEmail( {
		templateName: "approved-body-request",
		props: {
			user: { name: user?.firstName },
			schemeName: scheme?.name,
		},
		to: user?.email,
	} );
};

// Admin approves a submitted (pending) approved body by minting a brand new
// APPROVEDBODY record from the data the certifier submitted, then attaching it.
export const approveNewApprovedBody = async (subscription: Subscription) => {
	"use server";

	const pendingAB = subscription.pendingAB;

	if (!pendingAB) {
		throw new Error("Subscription has no pending approved body to approve");
	}

	const abId = await getCounter("APPROVEDBODY");

	if (abId < 1) {
		throw new Error("Error generating approved body id");
	}

	const now = Math.floor(Date.now() / 1000);

	// riasId, scheme, approved and expires are what the BSD register export
	// keys on - a body without them is silently dropped from the register.
	await putItem({
		pk: `APPROVEDBODY#${abId}`,
		sk: padId(abId, RIASRecordType.APPROVEDBODY),
		recordType: RIASRecordType.APPROVEDBODY,
		riasId: abId,
		scheme: schemeData(subscription.scheme)?.prefix ?? null,
		name: pendingAB.address?.title ?? pendingAB.address?.name ?? null,
		status: "active",
		approved: now,
		expires: subscription.expires ?? null,
		address: pendingAB.address,
		coordinator: pendingAB.coordinator,
		deputy: pendingAB.deputy,
	});

	await updateDB(subscription.pk as string, subscription.sk as string, {
		approvedBodyId: String(abId),
		status: SubscriptionState.ACTIVE,
		pendingAB: null,
	});

	return abId;
};

// Admin attaches an existing approved body to the subscription instead of
// creating a new one, discarding the pending submission.
export const assignExistingApprovedBody = async (
	subscription: Subscription,
	approvedBodyId: string,
) => {
	"use server";

	await updateDB(subscription.pk as string, subscription.sk as string, {
		approvedBodyId,
		status: SubscriptionState.ACTIVE,
		pendingAB: null,
	});

	// Keep the body's register dates in step with the subscription it now serves.
	const [ab] = (await queryByPK(`APPROVEDBODY#${approvedBodyId}`, "")) ?? [];

	if (ab && subscription.expires) {
		const updates: Record<string, any> = {};

		if (!ab.approved) updates.approved = Math.floor(Date.now() / 1000);
		if (!ab.expires || subscription.expires > ab.expires) updates.expires = subscription.expires;

		if (Object.keys(updates).length) {
			await updateDB(ab.pk, ab.sk, updates);
		}
	}
};

// Admin moves a certifier who has passed the practical test on to submitting
// their Approved Body.
export const requireApprovedBody = async (subscription: Subscription) => {
	"use server";

	const admin = await getUserFromEvent();

	if (!admin?.groups?.includes(ADMIN)) throw new Error("Unauthorized");

	if (subscription.status !== SubscriptionState.PRACTICAL) {
		throw new Error("Only a subscription at the practical stage can be moved on to Approved Body submission");
	}

	await updateDB(subscription.pk as string, subscription.sk as string, {
		status: SubscriptionState.REQUIRES_BODY,
	});
};

// Subscription lifecycle is a strict one-way pipeline: an active subscription
// must be suspended before it can be terminated - it cannot be terminated directly.
export const suspendSubscription = async (subscription: Subscription) => {
	"use server";

	if (subscription.status !== SubscriptionState.ACTIVE) {
		throw new Error("Only an active subscription can be suspended");
	}

	await updateDB(subscription.pk as string, subscription.sk as string, {
		status: SubscriptionState.SUSPENDED,
		suspended: Math.floor(Date.now() / 1000),
	});
};

export const terminateSubscription = async (subscription: Subscription) => {
	"use server";

	if (subscription.status !== SubscriptionState.SUSPENDED) {
		throw new Error("A subscription must be suspended before it can be terminated");
	}

	await updateDB(subscription.pk as string, subscription.sk as string, {
		status: SubscriptionState.TERMINATED,
		terminated: Math.floor(Date.now() / 1000),
	});
};

const generateSubscriptionName = async (schemeSlug: string) => {
	"use server";

	const scheme = schemeData(schemeSlug);

	if (!scheme) {
		throw new Error("Scheme not found: " + schemeSlug);
	}

	const subId = await getCounter("SUBSCRIPTION");

	if (subId < 1) {
		throw new Error("Error generating subscriptionId");
	}

	return `${scheme.prefix}-D-${ subId.toFixed(0).padStart(7, "0")}`;
};


// --- Manual (bank transfer) payments ---------------------------------------
//
// A bank transfer never reaches the Stripe webhook, so none of the automation a
// card payment triggers ever runs. These two let an admin paste the invoice
// number off the Xero invoice, see what it pays for, and then run exactly what
// the Stripe flow runs: record the payment in Xero, then paymentRecieved().

// What paymentRecieved() will actually do to this record, in plain English.
// Mirrors the switch in that function - a subscription in any other state is
// simply not handled there, so confirming would change nothing.
const describePaymentEffect = (
	item: any,
): { summary: string; effect: string | null } => {
	if (item.recordType === RIASRecordType.CERTIFICATE) {
		return {
			summary: `Certificate - ${item.name}`,
			effect:
				item.status === CertificateState.PAID
					? null
					: "Marks the certificate paid.",
		};
	}

	if (item.recordType !== RIASRecordType.SUBSCRIPTION) {
		return { summary: `${item.recordType} - ${item.name ?? item.sk}`, effect: null };
	}

	const scheme = schemeData(item.scheme)?.name ?? item.scheme;

	switch (item.status) {
		case SubscriptionState.PENDING:
		case SubscriptionState.APPLYING:
			return {
				summary: `New subscription - ${scheme}`,
				effect:
					schemeData(item.scheme)?.notScheme
						? "Makes them a certifier, starts CPD membership (20 years) and sends the subscription complete email."
						: "Makes them a certifier, moves the subscription to assessing for a year and sends the subscription complete email.",
			};

		case SubscriptionState.ACTIVE:
		case SubscriptionState.RENEWING:
			return {
				summary: `Subscription renewal - ${scheme}`,
				effect: "Sets the subscription active and extends its expiry by a year.",
			};

		case SubscriptionState.SUSPENDED:
		case SubscriptionState.ASSESSING:
		case SubscriptionState.PENDING_BODY:
		case SubscriptionState.PRACTICAL:
			return {
				summary: `Subscription (${item.status}) - ${scheme}`,
				effect: "Extends the expiry by a year. The status is left as it is.",
			};

		default:
			return { summary: `Subscription (${item.status}) - ${scheme}`, effect: null };
	}
};

export const lookupManualPayment = async (invoiceNumber: string) => {
	"use server";

	const admin = await getUserFromEvent();

	if (!admin?.groups?.includes(ADMIN)) return { error: "Unauthorized" };

	const number = invoiceNumber?.trim();

	if (!number) return { error: "Enter an invoice number" };

	const invoice = await getInvoiceByNumber(number);

	if ("error" in invoice) return { error: invoice.error };
	if (!invoice.invoiceId) return { error: "Xero returned an invoice with no id" };

	// We store the Xero GUID and never the number, so the match has to go
	// through Xero before it can reach one of our records.
	const r = await runQueryCommand({
		TableName: RIASTABLE,
		IndexName: "gsiInvoiceId",
		KeyConditionExpression: "invoiceId = :id",
		ExpressionAttributeValues: { ":id": invoice.invoiceId },
	});

	const item = r?.items?.[0];

	if (!item) {
		return { error: `${number} exists in Xero but matches no certificate or subscription` };
	}

	const { summary, effect } = describePaymentEffect(item);
	const user = await getProfile(parseInt(item.pk.split("#")[1], 10));

	const warnings: string[] = [];

	if ((invoice.amountDue ?? 0) <= 0) {
		warnings.push("This invoice is already settled in Xero - no further payment will be recorded there.");
	}

	if (!effect) {
		warnings.push(`Nothing is automated for a ${String(item.recordType).toLowerCase()} in state "${item.status}" - confirming would record the payment but change nothing else.`);
	}

	if (
		item.recordType === RIASRecordType.SUBSCRIPTION &&
		[SubscriptionState.ACTIVE, SubscriptionState.RENEWING].includes(item.status)
	) {
		warnings.push("Each run extends the expiry by another year - only confirm once.");
	}

	return {
		invoiceNumber: invoice.invoiceNumber ?? number,
		invoiceId: invoice.invoiceId,
		xeroStatus: invoice.status ?? null,
		total: invoice.total ?? null,
		amountDue: invoice.amountDue ?? null,
		contact: invoice.contact?.name ?? user?.name ?? "",
		lineDescriptions: invoice.lineDescriptions ?? [],
		summary,
		effect,
		warnings,
		record: { sk: item.sk, recordType: item.recordType, status: item.status, name: item.name },
		user: user ? { userId: user.userId, name: user.name, email: user.email } : null,
	};
};

export const confirmManualPayment = async (invoiceNumber: string) => {
	"use server";

	const admin = await getUserFromEvent();

	if (!admin?.groups?.includes(ADMIN)) return { error: "Unauthorized" };

	// Re-resolve from the number rather than trusting ids sent back by the browser.
	const found = await lookupManualPayment(invoiceNumber);

	if ("error" in found) return { error: found.error };

	const outstanding = found.amountDue ?? 0;

	// An invoice settled in Xero already (reconciled, or a repeat confirm) must
	// not get a second payment against it.
	if (outstanding > 0) {
		// Book it to the bank account the transfer arrived in. Left to default it
		// goes to the Stripe account and Xero shows a card payment that never was.
		const payment = await markInvoiceAsPaid(found.invoiceId, outstanding, {
			accountID: getServerEnv().xeroBankAccountId,
			reference: "Bank transfer",
		});

		if (!payment) {
			return { error: "Could not record the payment in Xero - see the server log. Nothing else has been changed." };
		}
	}

	try {
		await paymentRecieved(found.invoiceId);
	} catch (err) {
		console.error("Manual payment automation failed", err);

		return { error: `The payment was recorded in Xero but the automation failed: ${(err as Error).message}` };
	}

	return {
		success: true,
		invoiceNumber: found.invoiceNumber,
		summary: found.summary,
		markedPaidInXero: outstanding > 0,
	};
};
